import { ProductReflectionsQuery } from '@graphql/queries/__generated__/reflection.generated'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import DOMPurify from 'dompurify'
import _ from 'lodash'
import React, { useMemo, useRef, useState } from 'react'
import ReflectionCard from '../../components/Product/ReflectionCard'
import useLocalStorage from '../../hooks/useLocalStorage'
import { AdWithImageFragment } from '@graphql/fragments/__generated__/Ad.generated'
import { Link } from 'react-router-dom'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import PlaceHolder from 'images/placeholder-img.svg'
import { CLIENT_URL } from '../../../constants'
import { ProductListImageSizeEnum } from '../../../generated/gqlTypes'

export const STORAGE_GRID_NAME = 'categoryGridClass'

export enum REFLECTIONS_IMG_SIZE_ENUM {
  SMALL = 'product-grid_7',
  MEDIUM = 'product-grid_5',
  LARGE = 'product-grid_3',
}

export const DEFAULT_IMG_SIZE = REFLECTIONS_IMG_SIZE_ENUM.MEDIUM

// One ad per AD_EVERY_N_CARDS product cards. Inside its block an ad is placed at a random offset in
// [AD_MIN_OFFSET, AD_MAX_OFFSET], so two neighbouring ads are always at least 2 * AD_MIN_OFFSET cards apart.
export const AD_EVERY_N_CARDS = 50
const AD_MIN_OFFSET = 10
const AD_MAX_OFFSET = AD_EVERY_N_CARDS - AD_MIN_OFFSET - 1

export const getAdsCountForCards = (cardsCount: number): number => Math.ceil(cardsCount / AD_EVERY_N_CARDS)

export const IMG_SIZE_TO_BACKEND_ENUM: Record<REFLECTIONS_IMG_SIZE_ENUM, ProductListImageSizeEnum> = {
  [REFLECTIONS_IMG_SIZE_ENUM.SMALL]: ProductListImageSizeEnum.Small,
  [REFLECTIONS_IMG_SIZE_ENUM.MEDIUM]: ProductListImageSizeEnum.Medium,
  [REFLECTIONS_IMG_SIZE_ENUM.LARGE]: ProductListImageSizeEnum.Large,
}

export const BACKEND_ENUM_TO_IMG_SIZE: Record<ProductListImageSizeEnum, REFLECTIONS_IMG_SIZE_ENUM> = {
  [ProductListImageSizeEnum.Small]: REFLECTIONS_IMG_SIZE_ENUM.SMALL,
  [ProductListImageSizeEnum.Medium]: REFLECTIONS_IMG_SIZE_ENUM.MEDIUM,
  [ProductListImageSizeEnum.Large]: REFLECTIONS_IMG_SIZE_ENUM.LARGE,
}

type IReflectionEdges = ProductReflectionsQuery['specifics']['edges']

interface IProps {
  specificsData: IReflectionEdges
  imgSizeClass: REFLECTIONS_IMG_SIZE_ENUM
  loading?: boolean
  categoryDescription?: string
  categoryName?: string
  ads: AdWithImageFragment[]
  // More products can be loaded; while true, each shown ad hides one trailing product.
  hasMore?: boolean
  // Render the redesigned product cards (see ReflectionCard `cardView`).
  cardView?: boolean
  // Wholesale mode: the cards show wholesale prices and add to the wholesale cart (see ReflectionCard `wholesale`).
  wholesale?: boolean

  onAdClick?(adId: string): void
}

const range = _.range(20)
const ReflectionsListSkeleton: React.FC<{ cardView?: boolean }> = ({ cardView }) => {
  return (
    <>
      {range.map((item, i) => (
        <div key={i} className={cardView ? 'product-card' : ''}>
          <div
            className={`relative mb-10 ${cardView ? 'rounded-lg overflow-hidden' : ''}`}
            style={{ paddingBottom: '138%' }}
          >
            <Skeleton className='absolute top-0 bottom-0 left-0 right-0' variant='rectangular' height='100%' />
          </div>
          <Skeleton variant='text' />
          <Skeleton variant='text' />
        </div>
      ))}
    </>
  )
}

type MEDIA_SIZE = {
  xs: boolean
  sm: boolean
  md: boolean
  lg: boolean
}

const getIndexForGridRow = (mediaSize: MEDIA_SIZE, gridClass: REFLECTIONS_IMG_SIZE_ENUM): number => {
  switch (gridClass) {
    case REFLECTIONS_IMG_SIZE_ENUM.SMALL:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 3
      } else if (mediaSize.md) {
        return 5
      } else {
        return 7
      }
    case REFLECTIONS_IMG_SIZE_ENUM.MEDIUM:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 3
      } else if (mediaSize.md) {
        return 4
      } else {
        return 5
      }
    case REFLECTIONS_IMG_SIZE_ENUM.LARGE:
      if (mediaSize.xs) {
        return 2
      } else if (mediaSize.sm) {
        return 2
      } else if (mediaSize.md) {
        return 3
      } else {
        return 3
      }
  }
  return 7
}

const SpecificsList: React.FC<IProps> = ({
  specificsData,
  imgSizeClass,
  loading,
  categoryDescription,
  categoryName,
  ads,
  hasMore = false,
  cardView = false,
  wholesale = false,
  onAdClick,
}) => {
  const theme = useTheme()
  const [, , getStorageValue] = useLocalStorage(STORAGE_GRID_NAME, DEFAULT_IMG_SIZE)
  const xs = useMediaQuery(theme.breakpoints.only('xs'))
  const sm = useMediaQuery(theme.breakpoints.only('sm'))
  const md = useMediaQuery(theme.breakpoints.only('md'))
  const lg = useMediaQuery(theme.breakpoints.up('lg'))
  const adOffsetsRef = useRef<number[]>([])
  // Only one card keeps its quick add-to-cart panel open.
  const [openPanelSpecId, setOpenPanelSpecId] = useState<string | null>(null)
  const onGooglePushDataLayer = (reflection, category, position, action) => e => {
    // googlePushProductClick(reflection, category, position, action)
  }
  const showBigImg =
    [REFLECTIONS_IMG_SIZE_ENUM.LARGE, REFLECTIONS_IMG_SIZE_ENUM.MEDIUM].includes(imgSizeClass || getStorageValue()) ||
    sm ||
    xs
  const indexRow = getIndexForGridRow(
    {
      xs,
      sm,
      md,
      lg,
    },
    imgSizeClass || getStorageValue()
  )

  // The i-th ad goes into the i-th block of AD_EVERY_N_CARDS cards. Its offset is picked once, among the cards
  // that stay visible after hiding one product per ad (see visibleSpecifics), so the ad shows up right away and
  // keeps its position across pagination.
  const adPositions = useMemo(() => {
    if (!ads.length) {
      adOffsetsRef.current = []
      return {}
    }
    const result: { [key: number]: AdWithImageFragment } = {}
    ads.forEach((ad, block) => {
      const blockStart = block * AD_EVERY_N_CARDS
      if (adOffsetsRef.current[block] === undefined) {
        const lastVisibleIndex = specificsData.length - 1 - (block + 1)
        const maxOffset = Math.min(AD_MAX_OFFSET, lastVisibleIndex - blockStart)
        if (maxOffset < AD_MIN_OFFSET) {
          return
        }
        adOffsetsRef.current[block] = _.random(AD_MIN_OFFSET, maxOffset)
      }
      result[blockStart + adOffsetsRef.current[block]] = ad
    })
    return result
  }, [ads, specificsData.length])

  // Each shown ad takes the place of the last loaded product, so the list keeps the usual number of cards.
  // Hidden products appear after the next load more. On the last page nothing is hidden.
  const visibleSpecifics = useMemo(() => {
    if (!hasMore) {
      return specificsData
    }
    let hiddenCount = 0
    Object.keys(adPositions)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach(position => {
        // The ad is rendered after card `position`, which must stay visible once one more product is hidden.
        if (position < specificsData.length - hiddenCount - 1) {
          hiddenCount += 1
        }
      })
    return specificsData.slice(0, specificsData.length - hiddenCount)
  }, [specificsData, adPositions, hasMore])

  return (
    <>
      {!specificsData?.length && !loading && (
        <Typography variant='h5'>Sorry, no matches found for your request. Try again or shop new arrivals.</Typography>
      )}
      <div
        className={`category-products__list product-grid ${imgSizeClass || getStorageValue()} ${
          cardView ? 'product-grid--cards' : ''
        }`}
      >
        {visibleSpecifics.map((el, i) => {
          const spec = el.node
          const ad = adPositions[i]
          const adSmallImageUrl = ad?.images.filter(img => img.device === 'xs')[0]?.image
          const adBigImageUrl = ad?.images.filter(img => img.device !== 'xs')[0]?.image
          return (
            <React.Fragment key={spec.id}>
              {!!categoryDescription && i === indexRow && (
                <div className='grid-row flex items-center py-24'>
                  <div
                    className='description'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(categoryDescription),
                    }}
                  />
                </div>
              )}
              <div
                className={`product-thumb text-center ${cardView ? 'product-card' : ''} ${
                  cardView && openPanelSpecId === spec.id ? 'product-card--active' : ''
                }`}
              >
                <ReflectionCard
                  spec={spec}
                  onGooglePushDataLayer={onGooglePushDataLayer}
                  showBigImg={showBigImg}
                  idx={i}
                  categoryName={categoryName}
                  showFavoritesButton
                  cardView={cardView}
                  wholesale={wholesale}
                  isPanelOpen={openPanelSpecId === spec.id}
                  onTogglePanel={open => setOpenPanelSpecId(open ? spec.id : null)}
                />
              </div>
              {ad && (
                <div className={`product-thumb text-center ${cardView ? 'product-card product-card--ad' : ''}`}>
                  <div className='relative h-full'>
                    <a
                      href={ad.url}
                      onClick={() => onAdClick(ad.id)}
                      aria-label='product'
                      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
                    >
                      {showBigImg && (
                        <LazyLoadImage
                          alt={ad.title}
                          effect='opacity'
                          placeholderSrc={PlaceHolder}
                          src={adBigImageUrl || adSmallImageUrl || PlaceHolder}
                          className='h-full w-full object-cover'
                          wrapperProps={{ style: { display: 'block' } }}
                        />
                      )}
                      {!showBigImg && (
                        <LazyLoadImage
                          alt={ad.title}
                          effect='opacity'
                          placeholderSrc={PlaceHolder}
                          src={adSmallImageUrl || adBigImageUrl || PlaceHolder}
                          className='h-full w-full object-cover'
                          wrapperProps={{ style: { display: 'block' } }}
                        />
                      )}
                    </a>
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
        {!!specificsData && !!categoryDescription && visibleSpecifics.length <= indexRow && (
          <div className='grid-row flex items-center py-24'>
            <div
              className='description'
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(categoryDescription),
              }}
            />
          </div>
        )}
        {loading && !specificsData?.length && <ReflectionsListSkeleton cardView={cardView} />}
      </div>
    </>
  )
}

export default SpecificsList
